import { previewValue, publicIdFormat, type FormatSpec } from "./format";
import {
  ALL_PUBLIC_ID_RESOURCE_TYPES,
  MINTED_PUBLIC_ID_SUFFIX_LENGTH,
  type PublicId,
  type PublicIdResourceType,
} from "./public-id";

/**
 * JSON-schema / OpenAPI key every id helper stamps with its kind, so a
 * conformance walk can tell a checked id from a free-form string.
 */
export const ID_KIND_META_KEY = "x-id-kind";

/** A task by its public id or by its number (`T-123`). */
export type TaskNumberRef = string & { readonly __taskNumberRef: true };
export type TaskRef = PublicId<"task"> | TaskNumberRef;

const TASK_NUMBER = /^[Tt]-[1-9][0-9]{0,9}$/;
const BARE_TASK_NUMBER = /^[1-9][0-9]{0,9}$/;
const LEADING_TASK_NUMBER = /^([Tt]-[1-9][0-9]{0,9})(?![0-9])./s;

/**
 * A task public id (`tsk_…`, strict) or task number (`T-123`, whole string).
 * Agent tools also take the bare number (`123`); the API does not, since a
 * bare number there was always looked up as a public id.
 */
export function taskRefFormat(
  opts: { allowBareNumber?: boolean; howToResolve?: string } = {},
): FormatSpec<TaskRef> {
  const taskId = publicIdFormat("task");
  const example = opts.allowBareNumber ? "tsk_…, T-123 or 123" : "tsk_… or T-123";
  return {
    name: "task id or number",
    example,
    idKind: "task_ref",
    pattern: new RegExp(
      `^(?:tsk_[0-9a-f]{${MINTED_PUBLIC_ID_SUFFIX_LENGTH}}|[Tt]-[1-9][0-9]{0,9}${opts.allowBareNumber ? "|[1-9][0-9]{0,9}" : ""})$`,
    ),
    howToResolve:
      opts.howToResolve ?? `Pass the task's public id or its number, in the form ${example}.`,
    ...(opts.howToResolve ? { whereToFind: opts.howToResolve } : {}),
    test: (value): value is TaskRef =>
      typeof value === "string" &&
      (TASK_NUMBER.test(value) ||
        (opts.allowBareNumber === true && BARE_TASK_NUMBER.test(value)) ||
        taskId.test(value)),
    describeMismatch: (value) => {
      if (typeof value !== "string") return undefined;
      const number = LEADING_TASK_NUMBER.exec(value)?.[1];
      if (number) return extraCharacters(value, number);
      return taskId.describeMismatch?.(value);
    },
  };
}

/** The shape of `agents.slug`, which the `agents_slug_fill` trigger derives from the name. */
const AGENT_SLUG = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

/**
 * An agent's public id or its slug. Configuration holds the slug, which still names the agent in
 * another workspace; an id does not.
 */
export function agentRefFormat(opts: { howToResolve?: string } = {}): FormatSpec<string> {
  const agentId = publicIdFormat("agent");
  const example = "agt_… or claims-reviewer";
  return {
    name: "agent id or slug",
    example,
    idKind: "agent_ref",
    pattern: new RegExp(
      `^(?:agt_[0-9a-f]{${MINTED_PUBLIC_ID_SUFFIX_LENGTH}}|[a-z0-9]+(?:-[a-z0-9]+)*)$`,
    ),
    howToResolve: opts.howToResolve ?? `Pass the agent's public id or its slug, like ${example}.`,
    ...(opts.howToResolve ? { whereToFind: opts.howToResolve } : {}),
    test: (value): value is string =>
      typeof value === "string" && (AGENT_SLUG.test(value) || agentId.test(value)),
    describeMismatch: (value) => agentId.describeMismatch?.(value),
  };
}

function extraCharacters(value: string, clean: string) {
  return {
    what: `${previewValue(value)} has extra characters after the id; did you mean ${clean}?`,
    howToResolve: "Resend exactly that id.",
  };
}

/**
 * A public id of any of `types`, chosen by a sibling field such as
 * `resource_type`. `"any"` takes every registered type, for a lookup whose
 * handler names an unsupported type itself.
 */
export function polymorphicIdFormat(
  types: readonly PublicIdResourceType[] | "any",
  opts: { label?: string; howToResolve?: string } = {},
): FormatSpec<string> {
  if (types !== "any") {
    return publicIdFormat(types, opts);
  }
  const base = publicIdFormat(ALL_PUBLIC_ID_RESOURCE_TYPES, { label: opts.label ?? "Quovy" });
  return {
    ...base,
    example: "<prefix>_…",
    idKind: "public:any",
    pattern: new RegExp(`^[a-z]+_[0-9a-f]{${MINTED_PUBLIC_ID_SUFFIX_LENGTH}}$`),
    howToResolve:
      opts.howToResolve ??
      `Pass a Quovy public id: a type prefix, an underscore and ${MINTED_PUBLIC_ID_SUFFIX_LENGTH} lowercase hex characters.`,
    ...(opts.howToResolve ? { whereToFind: opts.howToResolve } : {}),
  };
}

// Whitespace, quotes, braces, brackets, commas and control characters never
// occur in any provider or internal id; they are what a garbled tool call adds.
const FORBIDDEN_ID_CHARACTER = /[\s"'`{}[\],<>\\\p{Cc}]/u;

type TokenIdSpec = {
  /** Short noun, e.g. `"Microsoft Graph drive id"`. */
  name: string;
  example: string;
  /** The whole value; anchored. */
  pattern: RegExp;
  howToResolve: string;
};

function tokenIdFormat(idKind: string, spec: TokenIdSpec): FormatSpec<string> {
  return {
    name: spec.name,
    example: spec.example,
    idKind,
    pattern: spec.pattern,
    howToResolve: spec.howToResolve,
    whereToFind: spec.howToResolve,
    test: (value): value is string =>
      typeof value === "string" && !FORBIDDEN_ID_CHARACTER.test(value) && spec.pattern.test(value),
    describeMismatch: (value) => {
      if (typeof value !== "string") return undefined;
      const cut = value.search(FORBIDDEN_ID_CHARACTER);
      if (cut <= 0) return undefined;
      const clean = value.slice(0, cut);
      return spec.pattern.test(clean) ? extraCharacters(value, clean) : undefined;
    },
  };
}

const GUID = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
// Graph ids are base64 / base64url with provider punctuation (`!` in drive ids, `=` padding).
const GRAPH_TOKEN = /^[A-Za-z0-9!._~:@=+/-]{1,1024}$/;
const URL_SAFE_TOKEN = /^[A-Za-z0-9._~:@=+/-]{1,1024}$/;

/**
 * Every provider id an agent tool or API request accepts, one entry per kind.
 * A field picks its kind here rather than declaring a free-form string.
 */
export const EXTERNAL_ID_FORMATS = {
  github_repository: {
    name: "GitHub repository id",
    example: "123456789",
    pattern: /^[1-9][0-9]{0,15}$/,
    howToResolve: "Select the repository from the installed GitHub repository picker.",
  },
  github_installation: {
    name: "GitHub App installation id",
    example: "12345678",
    pattern: /^[1-9][0-9]{0,15}$/,
    howToResolve: "Select an installed GitHub account from the account picker.",
  },
  entra_tenant: {
    name: "Microsoft Entra tenant id",
    example: "a GUID such as 72f988bf-86f1-41af-91ab-2d7cd011db47",
    pattern: GUID,
    howToResolve: "Copy the Directory (tenant) ID from the Entra admin center app overview.",
  },
  entra_client: {
    name: "Microsoft Entra application (client) id",
    example: "a GUID such as 00000003-0000-0000-c000-000000000000",
    pattern: GUID,
    howToResolve: "Copy the Application (client) ID from the Entra app registration overview.",
  },
  graph_drive: {
    name: "Microsoft Graph drive id",
    example: "b!…",
    pattern: GRAPH_TOKEN,
    howToResolve: "Pass a drive id exactly as a previous listing returned it.",
  },
  graph_drive_item: {
    name: "Microsoft Graph drive item id",
    example: "01ABC…",
    pattern: GRAPH_TOKEN,
    howToResolve: "Pass a file or folder id exactly as a previous listing returned it.",
  },
  provider_file: {
    name: "connected-drive file id",
    example: "1A2b3C… (Google Drive) or 01ABC… (OneDrive)",
    pattern: GRAPH_TOKEN,
    howToResolve: "Pass a file id exactly as the integration's file listing returned it.",
  },
  graph_drive_item_version: {
    name: "Microsoft Graph file version id",
    example: "1.0",
    pattern: GRAPH_TOKEN,
    howToResolve: "Pass a version id exactly as the file's version list returned it.",
  },
  mail_folder: {
    name: "mail folder id",
    example: "AAMkAG… or INBOX",
    pattern: GRAPH_TOKEN,
    howToResolve: "Pass a folder id exactly as the mailbox's folder list returned it.",
  },
  mail_message: {
    name: "provider message id",
    example: "AAMkAG… or 18c2f…",
    pattern: GRAPH_TOKEN,
    howToResolve: "Pass a message id exactly as a previous message listing returned it.",
  },
  gmail_thread: {
    name: "Gmail thread id",
    example: "18c2f…",
    pattern: /^[A-Za-z0-9_-]{1,256}$/,
    howToResolve: "Pass a thread id exactly as a previous Gmail search returned it.",
  },
  gmail_label: {
    name: "Gmail label id",
    example: "INBOX or Label_123",
    pattern: /^[A-Za-z0-9_-]{1,256}$/,
    howToResolve: "Pass a label id exactly as the Gmail label list returned it.",
  },
  gmail_filter: {
    name: "Gmail filter id",
    example: "ANe1Bm…",
    pattern: /^[A-Za-z0-9_-]{1,256}$/,
    howToResolve: "Pass a filter id exactly as the Gmail filter list returned it.",
  },
  google_drive_file: {
    name: "Google Drive file id",
    example: "1A2b3C…",
    pattern: /^[A-Za-z0-9_-]{1,256}$/,
    howToResolve: "Pass a file or folder id exactly as a previous Drive listing returned it.",
  },
  google_spreadsheet: {
    name: "Google Sheets spreadsheet id",
    example: "1A2b3C…",
    pattern: /^[A-Za-z0-9_-]{1,256}$/,
    howToResolve: "Pass the id from the spreadsheet's URL (the part after /d/).",
  },
  google_calendar: {
    name: "Google Calendar id",
    example: "primary or …@group.calendar.google.com",
    pattern: /^[A-Za-z0-9._%+@#-]{1,320}$/,
    howToResolve: "Pass primary or a calendar id exactly as the calendar list returned it.",
  },
  google_calendar_event: {
    name: "Google Calendar event id",
    example: "a1b2c3… or a1b2c3…_20260101T100000Z",
    pattern: /^[A-Za-z0-9_-]{1,1024}$/,
    howToResolve: "Pass an event id exactly as a previous event listing returned it.",
  },
  workos_organization: {
    name: "WorkOS organization id",
    example: "org_01H…",
    pattern: /^org_[0-9A-Z]{26}$/,
    howToResolve:
      "Copy the organization id (org_ followed by 26 characters) from the WorkOS dashboard.",
  },
  aws_access_key: {
    name: "AWS access key id",
    example: "AKIA…",
    pattern: /^(?:AKIA|ASIA)[A-Z0-9]{16}$/,
    howToResolve:
      "Copy the access key id (20 characters starting AKIA or ASIA) from the IAM console.",
  },
  model: {
    name: "model id",
    example: "claude-sonnet-4-5 or us.anthropic.claude-…-v1:0",
    pattern: /^[A-Za-z0-9][A-Za-z0-9._:/@-]{0,199}$/,
    howToResolve: "Pass a model id exactly as the provider's model list names it.",
  },
  first_party_oauth_client: {
    name: "OAuth client id",
    example: "quovy-cli",
    pattern: /^[A-Za-z0-9][A-Za-z0-9._-]{0,127}$/,
    howToResolve: "Pass the client id your Quovy client was issued.",
  },
  integration_correlation: {
    name: "integration correlation id",
    example: "an id from an integration event's correlation_id",
    pattern: URL_SAFE_TOKEN,
    howToResolve: "Copy correlation_id from an integration event.",
  },
  // Entra's token endpoint takes a tenant's initial domain as well as its GUID.
  entra_tenant_or_domain: {
    name: "Microsoft Entra tenant id or domain",
    example: "72f988bf-86f1-41af-91ab-2d7cd011db47 or contoso.onmicrosoft.com",
    pattern: new RegExp(
      `^(?:${GUID.source.slice(1, -1)}|[A-Za-z0-9-]{1,63}\\.onmicrosoft\\.(?:com|us|de|cn))$`,
    ),
    howToResolve:
      "Copy the Directory (tenant) ID or the tenant's onmicrosoft.com domain from the Entra admin center overview.",
  },
  // Each authorization server mints its own form (GUID, `0oa…`, a metadata URL).
  outbound_oauth_client: {
    name: "OAuth client id",
    example: "0oa1b2c3d4 or 00000000-0000-0000-0000-000000000000",
    pattern: /^[A-Za-z0-9][A-Za-z0-9._~:@/+=%-]{0,511}$/,
    howToResolve: "Copy the client id exactly as the authorization server issued it.",
  },
} as const satisfies Record<string, TokenIdSpec>;

export type ExternalIdKind = keyof typeof EXTERNAL_ID_FORMATS;

export function externalIdFormat(kind: ExternalIdKind): FormatSpec<string> {
  return tokenIdFormat(`external:${kind}`, EXTERNAL_ID_FORMATS[kind]);
}

const UUID = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
const SLUG = /^[A-Za-z0-9][A-Za-z0-9._:-]{0,254}$/;

/**
 * Ids that are not public ids and not a provider's, yet still reach a
 * request: internal row keys, queue jobs, and keys a definition chose
 * (a review question, a fixture resource).
 */
export const OPAQUE_ID_FORMATS = {
  uuid: {
    name: "UUID",
    example: "123e4567-e89b-12d3-a456-426614174000",
    pattern: UUID,
    howToResolve: "Pass the UUID exactly as the API returned it.",
  },
  audit_event: {
    name: "audit event id",
    example: "evt_…",
    pattern: new RegExp(`^evt_[0-9a-f]{${MINTED_PUBLIC_ID_SUFFIX_LENGTH}}$`),
    howToResolve: "Pass an event_id exactly as the audit log list returned it.",
  },
  queue_job: {
    name: "job id",
    example: "a job id returned when the job was queued",
    pattern: /^[A-Za-z0-9][A-Za-z0-9._:-]{0,255}$/,
    howToResolve: "Pass the job id exactly as the queue response returned it.",
  },
  definition_key: {
    name: "key",
    example: "q1 or option_a",
    pattern: SLUG,
    howToResolve: "Pass the key exactly as the definition that offered it spells it.",
  },
  fixture_ref: {
    name: "fixture id",
    example: "eth_claim or tsk_…",
    pattern: SLUG,
    howToResolve: "Pass an id the scenario's fixture declares, or a live public id.",
  },
  // Shaped like a public id, of any prefix: free-form resource types and
  // stored rows carry prefixes no registry entry mints (a retired `rec_`).
  public_id_shape: {
    name: "public id",
    example: "crec_… or sub_…",
    pattern: new RegExp(`^[a-z]{1,8}_[0-9a-f]{${MINTED_PUBLIC_ID_SUFFIX_LENGTH}}$`),
    howToResolve: "Pass the id exactly as the API that owns the resource returned it.",
  },
  // System actors (`workos.dsync`) and non-resource targets (feature flags,
  // provider ids) are recorded too, so an audit actor/target is not always a public id.
  audit_subject: {
    name: "audit actor or target id",
    example: "usr_… or workos.dsync",
    pattern: /^[A-Za-z0-9][A-Za-z0-9._:@/+=-]{0,255}$/,
    howToResolve: "Copy actor_id or target_id exactly as an audit log entry returned it.",
  },
  // Minted by compare_records within one grader run; never stored or returned by the API.
  comparison: {
    name: "comparison id",
    example: "cmp_1",
    pattern: /^cmp_[0-9]{1,4}$/,
    howToResolve: "Pass the comparison_id exactly as compare_records returned it in this run.",
  },
  // Minted by the model provider (`call_…`, `toolu_…`) and stored in the transcript as is.
  tool_call: {
    name: "tool call id",
    example: "call_abc123",
    pattern: /^[A-Za-z0-9][A-Za-z0-9._:-]{0,255}$/,
    howToResolve: "Pass a tool call id exactly as read_agent_thread listed it under tool_calls.",
  },
  // The editor mints condition ids with nanoid, whose alphabet lets an id start with `_` or `-`.
  workflow_graph_key: {
    name: "workflow node, edge or condition id",
    example: "action_1 or e_trigger__action_1",
    pattern: /^[A-Za-z0-9_-][A-Za-z0-9._:-]{0,255}$/,
    howToResolve:
      "Use letters, digits, _ or - for a new id, and keep an existing graph's ids unchanged.",
  },
} as const satisfies Record<string, TokenIdSpec>;

export type OpaqueIdKind = keyof typeof OPAQUE_ID_FORMATS;

export function opaqueIdFormat(kind: OpaqueIdKind): FormatSpec<string> {
  return tokenIdFormat(`opaque:${kind}`, OPAQUE_ID_FORMATS[kind]);
}
